import React, { useState } from "react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import "./InFocusPaginationStyles.scss";
import styles from "./InFocusContent.module.scss";
import DataLoader from "../../components/custom-data-loader/DataLoader";
import InFocusCustomSkeleton from "./InFocusCustomSkeleton";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import CustomSwiper from "../../components/v2/custom-swiper-layout/CustomSwiper";
import { MyLearningWidgetData } from "../../utility/interfaces/educationLibraryInterfaces";
import { useTranslation } from "react-i18next";
import {
  CONTENT_STATUS_COMPLETED_ID,
  SAVE_CLICK_STATUS_ID,
} from "../../utility/constants/common-constants";
import {
  getPatientRoute,
  handleExternalLinkClick,
} from "../../utility/functions/common-functions";
import { useSaveContentStatusMutation } from "../../components/custom-content-modal/SaveContentStatusAPISlice";
import { useSaveContentClickMutation } from "../view-learning-content/services/SaveContentStatusAccessLogAPISlice";
import InFocusCard from "./InFocusDesktopCard";
import { Pagination, Navigation } from "swiper/modules";
import { useGetMyLearningWidgetContentQuery } from "../../pages/patient-dashboard/widgets/myLearning/services/GetMyLearningAPISlice";
import MyLearningCard from "../learning-card/MyLearningCard";
import { setLearningContent } from "../view-learning-content/services/LearningContentDataSlice";
import { useLocation, useNavigate } from "react-router-dom";
import EmptyComponent from "../../components/v2/custom-empty-error-component/EmptyComponent";
import CustomTooltip from "../../components/custom-tooltip/CustomTooltip";
import { InfoOutlined } from "@mui/icons-material";

const InFocusContent: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { values } = theme.breakpoints;
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const pendingStatuses = [
    t("inProgress"),
    t("pending"),
    t("taskPending"),
    t("notCompleted"),
    t("initiated"),
    t("notStarted"),
    "",
  ];
  const {
    patientProfileInfo: { patientProfileId, userName: patientUserName },
  } = useAppSelector((state) => state.patientProfileDetails);

  const patientPreferredLang = useAppSelector(
    (state) => state.patientOnboardingStatus.preferredLanguage
  );

  const [saveContentClick] = useSaveContentClickMutation();

  const [contentStatus] = useSaveContentStatusMutation();

  const {
    data: myLearningWidgetData,
    isLoading: myLearningWidgetLoading,
    error: myLearningWidgetDataError,
  } = useGetMyLearningWidgetContentQuery({
    patientId: patientProfileId,
    preferredLanguage: patientPreferredLang,
  });

  const showContetntDetails = async (content: MyLearningWidgetData) => {
    // Handle external link content as before
    if (content?.contentTypeCode === "URL") {
      if (content?.milestoneLearningUrls) {
        window.open(
          content?.milestoneLearningUrls,
          "_blank",
          "noopener,noreferrer"
        );
        if (content?.activityCompletionStatus !== t("completed")) {
          handleExternalLinkClick(
            content.casePlanMilestoneMappingId,
            CONTENT_STATUS_COMPLETED_ID,
            patientUserName || "Patient",
            patientProfileId,
            patientPreferredLang,
            dispatch,
            contentStatus
          );
        }
        return;
      }
    } else {
      // Dispatch content to Redux
      dispatch(
        setLearningContent({
          type: content?.contentTypeCode,
          category: content?.activitySubCategoryName,
          isNew: content?.isNew,
          videoUrl: content?.learningContentDto?.videoUrl,
          fullHtml: content?.learningContentDto?.fullHtml,
          title: content?.activityDisplayName,
          summarry: content?.learningContentDto?.summary,
          casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId,
          activityCompletionStatus: content?.activityCompletionStatus,
          patientProfileId,
          lang: content?.learningContentDto?.contentMetaLang,
          activityPoints: content?.activityPoints,
          thumbnail: content?.learningContentDto?.thumbnail,
          length: content?.learningContentDto?.length,
          educationalContentSourceId: content?.educationalContentSourceId,
          educationalContentIdentifier: content?.contentCode.toString(),
          navigationInfo: {
            isViewLearningContentEnabled: true,
            arrivedFrom: location.pathname,
          },
        })
      );

      // Navigate to the learning view
      navigate(getPatientRoute(patientProfileId, "/learning"));
    }

    // Save click mutation
    try {
      const body = {
        casePlanMilestoneMappingId: content?.casePlanMilestoneMappingId || "",
        patientProfileId: patientProfileId,
        planId: 1,
        caseId: 1,
        milestoneId: 1,
        contentIdentifier: content?.contentCode.toString(),
        contentWatchedDurationInMins: 1,
        action: SAVE_CLICK_STATUS_ID,
        educationalContentSourceId: content?.educationalContentSourceId,
      };
      
      await saveContentClick({
        body,
        patientPreferredLang,
      }).unwrap();
    } catch (err: any) {
      console.log(err, "saveContentClick");
    }
  };

  return (
    <>
      <DataLoader
        isLoading={myLearningWidgetLoading}
        error={myLearningWidgetDataError}
        data={myLearningWidgetData}
        loadingComponent={<InFocusCustomSkeleton />}
        errorComponent={
          <EmptyComponent
            message={t("anErrorOccurred")}
            textColor="error"
          />
        }
        emptyComponent={null}
      >
        {() => (
          <>
            <Box
              className={styles.myLearningContainer}
              mt={isSmallScreen ? "2.56rem" : "3.75rem"}
              mb={isSmallScreen ? "4rem" : 0}
            >
              {isSmallScreen && (
                <Box
                  display="flex"
                  alignItems="center"
                  mb={"1.56rem"}
                >
                  <Typography
                    textTransform={"uppercase"}
                    variant="bodyOverline"
                  >
                    {t("educationContent.inFocus")}
                  </Typography>
                  <CustomTooltip
                    title={t("educationContent.focusText")}
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                    useDarkTheme
                  >
                    <InfoOutlined
                      fontSize="small"
                      sx={{
                        color: "black",
                        fontSize: 16,
                        paddingLeft: 1,
                      }}
                    />
                  </CustomTooltip>
                </Box>
              )}
              <Box className={styles.inFocusSwiperWrapper}>
                <CustomSwiper<MyLearningWidgetData>
                  items={myLearningWidgetData}
                  modules={[Navigation, Pagination]}
                  swiperOptions={{
                    navigation: true,
                    pagination: {
                      clickable: true,
                      el: `.customSwiperPagination`,
                    },
                    spaceBetween: 12,
                    breakpoints: {
                      0: {
                        slidesPerView: "auto",
                        spaceBetween: 12,
                      },
                      [values.md]: {
                        slidesPerView: 1,
                        spaceBetween: 20,
                      },
                      [values.lg]: {
                        slidesPerView: 1,
                        spaceBetween: 20,
                      },
                      [values.xl]: {
                        slidesPerView: 1,
                        spaceBetween: 20,
                      },
                    },
                  }}
                  renderSlide={(educationContent, index) => {
                    const content = educationContent.learningContentDto;
                    if (!isSmallScreen) {
                      return (
                        <InFocusCard
                          key={index}
                          imageSrc={content?.thumbnail}
                          title={educationContent?.activityDisplayName}
                          subtitle={content?.summary}
                          contentType={educationContent?.contentTypeCode}
                          isNew={educationContent?.isNew}
                          activityPoints={educationContent?.activityPoints}
                          duration={content?.length}
                          activityCompletionStatus={
                            educationContent.activityCompletionStatus
                          }
                          isCompleted={
                            !pendingStatuses.includes(
                              educationContent.activityCompletionStatus
                            )
                          }
                          onClick={() => showContetntDetails(educationContent)}
                        />
                      );
                    }

                    return (
                      <MyLearningCard
                        key={index}
                        imageSrc={content?.thumbnail}
                        imageAlt={content?.title}
                        isNew={educationContent?.isNew}
                        duration={content?.length}
                        title={educationContent?.activityDisplayName}
                        height="300px"
                        contentType={educationContent?.contentTypeCode}
                        activityPoints={educationContent.activityPoints}
                        activityCompletionStatus={
                          educationContent.activityCompletionStatus
                        }
                        isCompleted={
                          !pendingStatuses.includes(
                            educationContent.activityCompletionStatus
                          )
                        }
                        onButtonClick={() =>
                          showContetntDetails(educationContent)
                        }
                      />
                    );
                  }}
                />
                <Box className="customSwiperPagination" />
              </Box>
            </Box>
          </>
        )}
      </DataLoader>
    </>
  );
};

export default InFocusContent;
